/**
 * The "what's new" walkthrough modal. Listens for
 * $e.featureAnnouncements.needs.prompt (see featureAnnouncementPrompt.js),
 * then shows the unseen feature announcements one at a time - each as an
 * ordered series of slides the user pages through with Next, or dismisses
 * early with the close button (both count as "seen", but are reported
 * separately so OP reports can tell finishes from skips).
 *
 * Each slide can carry an optional call-to-action (a URL or an in-app
 * event, mirroring how top-level announcements work - see
 * AnnouncementImageView.click) which is reported independently of
 * Next/Skip when tapped.
 *
 * Once the current feature is done, the next queued one (if any) is shown
 * immediately, until the queue is empty.
 */
import { Component, ojs, app, state, v, payload } from "modular-openscriptjs";
import { cleanDispose, eParse } from "../../helpers";
import { tool } from "../../fotastart";
import {
    completeFeatureAnnouncement,
    skipFeatureAnnouncement,
    recordSlideView,
    recordSlideClick,
} from "../../featureAnnouncementPrompt";

const h = app("h");
const broker = app("broker");

export default class FeatureAnnouncementModal extends Component {
    modal = null;
    queue = [];

    // { feature, slideIndex } while something is showing, otherwise null.
    current = state(null);

    render(...args) {
        return h.div({ class: "d-none" }, ...args);
    }

    $$featureAnnouncements = {
        needs: {
            prompt: (ed, event) => this.enqueue(ed, event),
        },
    };

    enqueue(ed, event) {
        const { message } = eParse(ed);
        const featureAnnouncements = message.get("featureAnnouncements", []);

        if (!Array.isArray(featureAnnouncements) || featureAnnouncements.length === 0) {
            return;
        }

        this.queue = [...this.queue, ...featureAnnouncements];

        if (!this.current.value) {
            this.showNextFeature();
        }
    }

    showNextFeature() {
        let next = this.queue.shift();

        while (next && (!Array.isArray(next.slides) || next.slides.length === 0)) {
            next = this.queue.shift();
        }

        if (!next) {
            this.current.value = null;
            if (this.modal?._isShown) this.modal.hide();
            return;
        }

        this.current.value = { feature: next, slideIndex: 0 };
        this.recordCurrentSlideView();

        if (!this.modal) {
            this.buildModal();
        }

        if (!this.modal._isShown) {
            this.modal.show();
        }
    }

    buildModal() {
        const content = h.div(
            { class: "modal-content border-0 rounded-4 shadow-lg overflow-hidden" },
            v(this.current, (s) => this.renderBody(s.value)),
        );

        this.modal = tool.plainModal(content, {
            data_bs_backdrop: "static",
            data_bs_keyboard: "false",
        });
    }

    renderMedia(slide) {
        if (slide.media_type === "video") {
            const embed = this.youtubeEmbedUrl(slide.media_path);

            if (embed) {
                return h.div(
                    { class: "ratio ratio-16x9 rounded-4 overflow-hidden mb-3 bg-dark" },
                    h.iframe({
                        src: embed,
                        allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share",
                        allowfullscreen: "allowfullscreen",
                        frameborder: "0",
                    }),
                );
            }

            return h.div(
                { class: "ratio ratio-16x9 rounded-4 overflow-hidden mb-3 bg-dark" },
                h.video({
                    src: slide.media_path,
                    controls: "controls",
                    class: "w-100 h-100",
                    style: "object-fit: contain;",
                }),
            );
        }

        if (slide.media_type === "image" || slide.media_type === "gif") {
            return h.div(
                { class: "rounded-4 overflow-hidden mb-3" },
                h.img({
                    src: slide.media_path,
                    class: "w-100",
                    style: "max-height: 260px; object-fit: cover;",
                    alt: slide.heading || "",
                }),
            );
        }

        return null;
    }

    youtubeEmbedUrl(url) {
        if (!url) return null;

        const match = String(url).match(
            /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{11})/,
        );

        return match ? `https://www.youtube.com/embed/${match[1]}` : null;
    }

    renderBody(current) {
        if (!current) {
            return h.div();
        }

        const { feature, slideIndex } = current;
        const slides = feature.slides || [];
        const slide = slides[slideIndex] || {};
        const isLast = slideIndex >= slides.length - 1;

        return h.div(
            { class: "modal-body p-4 text-center position-relative" },
            h.button({
                class: "btn-close position-absolute top-0 end-0 m-3",
                aria_label: "Skip",
                onclick: this.method("skip"),
            }),
            this.renderMedia(slide),
            slide.heading
                ? h.h5({ class: "fw-bold mb-2 mt-2" }, slide.heading)
                : null,
            slide.body ? h.p({ class: "text-muted mb-0" }, slide.body) : null,
            slides.length > 1
                ? h.div(
                      { class: "d-flex justify-content-center gap-1 my-3" },
                      slides.map((s, i) =>
                          h.span({
                              class: `rounded-pill d-inline-block ${
                                  i === slideIndex ? "bg-primary" : "bg-secondary-subtle"
                              }`,
                              style: "width: 8px; height: 8px;",
                          }),
                      ),
                  )
                : h.div({ class: "my-3" }),
            slide.target_type
                ? h.button(
                      {
                          class: "btn btn-dark rounded-pill w-100 fw-bold mb-2 py-3",
                          onclick: this.method("handleCta"),
                      },
                      slide.cta_label || "Learn more",
                  )
                : null,
            h.button(
                {
                    class: "btn btn-outline-primary rounded-pill w-100 fw-bold",
                    onclick: this.method("next"),
                },
                isLast ? "Got it" : "Next",
            ),
        );
    }

    next() {
        const s = this.current.value;
        if (!s) return;

        const slides = s.feature.slides || [];

        if (s.slideIndex < slides.length - 1) {
            this.current.value = { feature: s.feature, slideIndex: s.slideIndex + 1 };
            this.recordCurrentSlideView();
            return;
        }

        this.completeCurrentFeature();
    }

    skip() {
        this.skipCurrentFeature();
    }

    handleCta() {
        const s = this.current.value;
        if (!s) return;

        const slides = s.feature.slides || [];
        const slide = slides[s.slideIndex];

        if (!slide || !slide.target_type) return;

        recordSlideClick(slide.id, { placement: "feature_announcement_modal" });

        if (slide.target_type === "event") {
            try {
                broker.send(
                    slide.target_value,
                    payload({ feature: s.feature, slide }),
                );
            } catch (e) {
                //
            }

            return;
        }

        const router = app("router");
        if (router.isQualifiedUrl(slide.target_value)) {
            let link = h.a({
                href: slide.target_value,
                target: slide.link_target || "_self",
                class: "d-none",
                parent: document.body,
            });

            link.click();
        } else {
            router.to(slide.target_value);
        }
    }

    recordCurrentSlideView() {
        const s = this.current.value;
        if (!s) return;

        const slides = s.feature.slides || [];
        const slide = slides[s.slideIndex];

        if (slide?.id) {
            recordSlideView(slide.id);
        }
    }

    completeCurrentFeature() {
        const s = this.current.value;

        if (s?.feature?.id) {
            completeFeatureAnnouncement(s.feature.id);
        }

        this.showNextFeature();
    }

    skipCurrentFeature() {
        const s = this.current.value;

        if (s?.feature?.id) {
            const slides = s.feature.slides || [];
            const slide = slides[s.slideIndex];
            skipFeatureAnnouncement(s.feature.id, slide?.id || null);
        }

        this.showNextFeature();
    }

    cleanUp() {
        super.cleanUp();
        if (this.modal) cleanDispose(this.modal);
    }
}

ojs(FeatureAnnouncementModal);
